import { ResolverTag } from '@ps/web-core-enums';
import { Translation, useResolver } from '@ps/web-shared-components';
import type { ReactElement, RefObject } from 'react';
import React, { useEffect, useState } from 'react';
import { renderToString } from 'react-dom/server';
import { FormattedMessage } from 'react-intl';
import { useSelector, useStore } from 'react-redux';
import Url from 'url-parse';

import { Providers } from '@swf/app';
import type { ObservableRequest } from '@swf/core/hooks/useRequest';
import useRequest from '@swf/core/hooks/useRequest';
import type { Store } from '@swf/core/store';
import { select } from '@swf/core/store';

import Gateway from '../Gateway/Gateway';
import Initialize from '../Initialize/Initialize';
import Result from '../Result/Result';
import dictionary from '../dictionary';
import type { InputProvider, JourneyContainer } from '../sdk/idscan-jcs';
import type { JourneyResult } from '../types';
import { checkCameraPermission, hasError } from '../utils';

import useAuthToken from './useAuthToken';
import {
  TXTWCL_As_we_may_need_to_use_your_cam_ELL,
  TXTWCL_NowX_please_capture_or_upload__ELL,
  TXTWCL_Take_a_photo_instead,
  TXTWCL_Upload_a_photo_instead,
  TXTWCL_Would_you_like_to_use_your_cam_ELL
} from './useIDScan.messages.json';
import useJourneyDefinitionId from './useJourneyDefinitionId';

import '../sdk/styles.scss';

let getJourneyResult: (container: JourneyContainer) => JourneyResult | undefined;

function sendJourneyRequest(
  {
    journeyContainer,
    request,
    onSuccess,
    journeyId
  }: {
    journeyContainer: JourneyContainer;
    request: ObservableRequest;
    onSuccess?: () => void;
    journeyId?: string;
  },
  notificationNumber: number
) {
  request({
    service: '/api/v1-preview/account/idscan/journey',
    method: 'POST',
    http: true,
    body: {
      journeyId,
      notificationNumber
    }
  });

  if (!hasError(getJourneyResult(journeyContainer))) {
    onSuccess?.();
  }
}

const useIDScan = (
  containerRef: RefObject<HTMLDivElement | null>,
  showError: (translationMessage?: ReactElement, isBlocking?: boolean) => void,
  defaultInputProvider?: InputProvider,
  onJourneyStart?: () => void,
  onJourneyComplete?: () => void,
  onJourneyInitialized?: () => void,
  onJourneyRefId?: (jid: string | undefined) => void
) => {
  const store = useStore() as Store;
  const request = useRequest();
  const license = useSelector(select.account.getLicense);
  const locale = useSelector(select.locale.get);
  const userId = useSelector(select.account.getUserId) as string;
  const token = useAuthToken(showError);

  const idscanServerUrl = useResolver(ResolverTag.IdscanHostedUrl);
  const journeyDefinitionId = useJourneyDefinitionId(token, idscanServerUrl, showError);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    let journeyContainer: JourneyContainer;
    let journeyId: string | undefined;
    let startNotificationSent = false;
    let endNotificationSent = false;

    const initialize = async () => {
      const isCameraEnabled = await checkCameraPermission();

      if (!isCameraEnabled) {
        showError(<Translation {...TXTWCL_As_we_may_need_to_use_your_cam_ELL} />, true);
        return;
      }

      const paramsLoaded = token && idscanServerUrl && journeyDefinitionId;

      paramsLoaded && setInitialized(true);
      paramsLoaded &&
        import(/* webpackChunkName: "IDScan" */ '../sdk/idscan-jcs').then(
          ({
            JourneyContainer,
            JourneyEvent,
            TemplateType,
            PreProcessor,
            Templates,
            FlowState
          }) => {
            const StateToGatewayMessagesMap = {
              [FlowState.ADDRESS]: {
                title: <FormattedMessage {...TXTWCL_NowX_please_capture_or_upload__ELL} />,
                camera: TXTWCL_Take_a_photo_instead.defaultMessage,
                upload: TXTWCL_Upload_a_photo_instead.defaultMessage
              },
              default: {
                title: <FormattedMessage {...TXTWCL_Would_you_like_to_use_your_cam_ELL} />,
                camera: TXTWCL_Take_a_photo_instead.defaultMessage,
                upload: TXTWCL_Upload_a_photo_instead.defaultMessage
              }
            };

            getJourneyResult = (container: JourneyContainer): JourneyResult | undefined => {
              if (journeyId) {
                const {
                  journey: { currentResult, highLevelResult }
                } = container.getJourneyState();
                return { currentResult, highLevelResult };
              }
            };

            const getTemplate = (element: React.ReactElement) =>
              renderToString(<Providers store={store}>{element}</Providers>);

            journeyContainer = new JourneyContainer({
              isVerboseLogEnabled: false,
              assetsDirectory: '/static/doc-verify/idscan/assets',
              token,
              fileUploadOnCameraCaptureJourneys: true,
              auth: false,
              backendUrl: `${new Url(idscanServerUrl).origin}:443/`,
              journeyDefinitionId,
              defaultInputProvider,
              container: containerRef.current as HTMLElement,
              smartCapture: {
                workerScriptUrl: '/static/doc-verify/idscan/ides-micro.js',
                asmScriptUrl: '/static/doc-verify/idscan/idesmicro_asm.js',
                timeout: 15000,
                timeLogsEnabled: false
              },
              additionalData: [
                {
                  name: 'UserID',
                  value: window.btoa(
                    [...new TextEncoder().encode(userId)]
                      .map((x) => String.fromCharCode(x))
                      .join('')
                  ),
                  step: '',
                  type: ''
                },
                { name: 'LicenseID', value: license.toString(), step: '', type: '' }
              ],
              dictionary,
              onJourneyEvent: (event, meta, state) => {
                switch (event) {
                  case JourneyEvent.JOURNEY_START: {
                    journeyId = meta.id;
                    onJourneyRefId?.(journeyId);
                    onJourneyStart?.();

                    break;
                  }

                  case JourneyEvent.TRANSFER_COMPLETE: {
                    if (
                      meta.name === 'upload' &&
                      meta.status === 'complete' &&
                      state.action === FlowState.IDENTITY_FRONT &&
                      !startNotificationSent
                    ) {
                      onJourneyInitialized?.();
                      sendJourneyRequest(
                        {
                          journeyContainer,
                          request,
                          journeyId
                        },
                        1
                      );
                      startNotificationSent = true;
                    }
                    break;
                  }

                  case JourneyEvent.JOURNEY_END: {
                    if (!endNotificationSent) {
                      sendJourneyRequest(
                        {
                          journeyContainer,
                          request,
                          onSuccess: onJourneyComplete,
                          journeyId
                        },
                        2
                      );
                      endNotificationSent = true;
                    }

                    break;
                  }
                  default:
                    break;
                }
              },
              templates: {
                [Templates.Initializing]: {
                  type: TemplateType.Function,
                  processor: PreProcessor.Mustache,
                  provider: () => getTemplate(<Initialize />)
                },
                [Templates.Gateway]: {
                  type: TemplateType.Function,
                  processor: PreProcessor.Mustache,
                  provider: () =>
                    getTemplate(
                      <Gateway
                        messages={
                          StateToGatewayMessagesMap[
                            journeyContainer.getJourneyState()
                              .action as keyof typeof StateToGatewayMessagesMap
                          ] || StateToGatewayMessagesMap.default
                        }
                      />
                    )
                },
                [Templates.Result]: {
                  type: TemplateType.Function,
                  processor: PreProcessor.Mustache,
                  provider: () =>
                    getTemplate(
                      <Result
                        journeyResult={getJourneyResult(journeyContainer)}
                        journey={journeyContainer.getJourneyState().journey}
                      />
                    )
                }
              }
            });
            journeyContainer.initialize();
          }
        );
    };

    !initialized && initialize();

    return () => {
      journeyContainer?.terminate();
    };
  }, [
    initialized,
    containerRef,
    defaultInputProvider,
    showError,
    idscanServerUrl,
    journeyDefinitionId,
    locale,
    onJourneyComplete,
    onJourneyStart,
    request,
    token,
    userId,
    license,
    onJourneyInitialized,
    onJourneyRefId,
    store
  ]);
};

export default useIDScan;
